/**
 * MCP Service - Model Context Protocol Integration
 * 
 * Manages connection to Supabase MCP server for real-time AI capabilities
 */

import { supabase } from './supabase';

export interface MCPStatus {
    connected: boolean;
    lastPing: number;
    serverHealth: 'online' | 'degraded' | 'offline';
    aiAgentActive: boolean;
    latency: number;
}

class MCPService {
    private status: MCPStatus = {
        connected: false,
        lastPing: 0,
        serverHealth: 'offline',
        aiAgentActive: false,
        latency: 0
    };

    private healthCheckInterval: NodeJS.Timeout | null = null;
    private listeners: ((status: MCPStatus) => void)[] = [];

    /**
     * Initialize MCP connection and start health monitoring
     */
    async initialize(): Promise<MCPStatus> {
        console.log('🔗 Initializing MCP connection...');

        try {
            const startTime = Date.now();

            // Test Supabase connection as MCP proxy
            const { data, error } = await supabase
                .from('profiles')
                .select('id')
                .limit(1);

            const latency = Date.now() - startTime;

            if (error) {
                console.warn('⚠️ MCP connection test failed:', error);
                this.updateStatus({
                    connected: false,
                    serverHealth: 'offline',
                    aiAgentActive: false,
                    latency: 0,
                    lastPing: Date.now()
                });
                return this.status;
            }

            // Connection successful
            this.updateStatus({
                connected: true,
                serverHealth: latency < 500 ? 'online' : 'degraded',
                aiAgentActive: true,
                latency,
                lastPing: Date.now()
            });

            console.log(`✅ MCP connected (${latency}ms latency)`);

            // Start periodic health checks
            this.startHealthMonitoring();

            return this.status;

        } catch (error) {
            console.error('❌ Mcp initialization error:', error);
            this.updateStatus({
                connected: false,
                serverHealth: 'offline',
                aiAgentActive: false,
                latency: 0,
                lastPing: Date.now()
            });
            return this.status;
        }
    }

    /**
     * Start periodic health monitoring (every 30 seconds)
     */
    private startHealthMonitoring() {
        if (this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval);
        }

        this.healthCheckInterval = setInterval(async () => {
            await this.healthCheck();
        }, 30000); // 30 seconds
    }

    /**
     * Perform a quick health check
     */
    private async healthCheck() {
        const startTime = Date.now();

        try {
            const { error } = await supabase
                .from('profiles')
                .select('id')
                .limit(1);

            const latency = Date.now() - startTime;

            if (error) {
                this.updateStatus({
                    ...this.status,
                    connected: false,
                    serverHealth: 'offline',
                    lastPing: Date.now()
                });
                return;
            }

            this.updateStatus({
                ...this.status,
                connected: true,
                serverHealth: latency < 500 ? 'online' : 'degraded',
                latency,
                lastPing: Date.now()
            });

        } catch (error) {
            this.updateStatus({
                ...this.status,
                connected: false,
                serverHealth: 'offline',
                lastPing: Date.now()
            });
        }
    }

    /**
     * Subscribe to MCP status updates
     */
    subscribe(callback: (status: MCPStatus) => void): () => void {
        this.listeners.push(callback);

        // Immediately notify with current status
        callback(this.status);

        // Return unsubscribe function
        return () => {
            this.listeners = this.listeners.filter(cb => cb !== callback);
        };
    }

    /**
     * Get current MCP status
     */
    getStatus(): MCPStatus {
        return { ...this.status };
    }

    /**
     * Update status and notify listeners
     */
    private updateStatus(newStatus: MCPStatus) {
        this.status = newStatus;
        this.listeners.forEach(callback => callback(newStatus));
    }

    /**
     * Disconnect and cleanup
     */
    disconnect() {
        if (this.healthCheckInterval) {
            clearInterval(this.healthCheckInterval);
            this.healthCheckInterval = null;
        }

        this.updateStatus({
            connected: false,
            serverHealth: 'offline',
            aiAgentActive: false,
            latency: 0,
            lastPing: Date.now()
        });

        console.log('🔌 MCP disconnected');
    }

    /**
     * Test AI agent responsiveness
     */
    async testAIAgent(): Promise<boolean> {
        try {
            // Test if Edge Functions are responsive
            const { data, error } = await supabase.functions.invoke('process-disaster-resume', {
                body: { test: true }
            });

            if (error) {
                console.warn('AI Agent test failed:', error);
                return false;
            }

            return true;
        } catch (error) {
            console.error('AI Agent test error:', error);
            return false;
        }
    }
}

// Singleton instance
export const mcpService = new MCPService();

export default mcpService;
